import { PublicClientApplication, AccountInfo } from "@azure/msal-browser";

const API_BASE = process.env.REACT_APP_FUNC_ENDPOINT || "";
const CLIENT_ID = process.env.REACT_APP_CLIENT_ID || "";

const msalConfig = {
  auth: {
    clientId: CLIENT_ID,
    authority: "https://login.microsoftonline.com/common",
    redirectUri: window.location.origin + window.location.pathname,
  },
  cache: {
    cacheLocation: "sessionStorage" as const,
  },
};

let msalInstance: PublicClientApplication | null = null;

function getMsal(): PublicClientApplication {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
  }
  return msalInstance;
}

export interface WebUser {
  id: string;
  email: string;
  displayName: string;
  microsoftId: string;
}

class WebAuthService {
  private static instance: WebAuthService;
  private cachedUser: WebUser | null = null;

  static getInstance(): WebAuthService {
    if (!WebAuthService.instance) {
      WebAuthService.instance = new WebAuthService();
    }
    return WebAuthService.instance;
  }

  getToken(): string | null {
    return localStorage.getItem("gantt-auth-token");
  }

  getCurrentUser(): WebUser | null {
    if (this.cachedUser) return this.cachedUser;
    const stored = localStorage.getItem("gantt-web-user");
    if (stored) {
      this.cachedUser = JSON.parse(stored);
      return this.cachedUser;
    }
    return null;
  }

  isAuthenticated(): boolean {
    return !!this.getToken() && !!this.getCurrentUser();
  }

  private setAuth(token: string, user: WebUser) {
    localStorage.setItem("gantt-auth-token", token);
    localStorage.setItem("gantt-web-user", JSON.stringify(user));
    this.cachedUser = user;
  }

  async register(email: string, password: string, displayName: string, verificationCode?: string): Promise<WebUser> {
    const res = await fetch(`${API_BASE}/api/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password, displayName, verificationCode }),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Registration failed");
    this.setAuth(data.token, data.user);
    return data.user;
  }

  async login(email: string, password: string): Promise<WebUser> {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Login failed");
    this.setAuth(data.token, data.user);
    return data.user;
  }

  async microsoftLogin(email: string, displayName: string, microsoftId: string): Promise<WebUser> {
    const res = await fetch(`${API_BASE}/api/auth/microsoft`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, displayName, microsoftId }),
    });

    if (!res.ok) throw new Error("Authentication failed");

    const data = await res.json();
    this.setAuth(data.token, data.user);
    return data.user;
  }

  async validateToken(): Promise<WebUser | null> {
    const token = this.getToken();
    if (!token) return null;

    try {
      const res = await fetch(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        this.logout();
        return null;
      }

      const data = await res.json();
      this.cachedUser = data.user;
      localStorage.setItem("gantt-web-user", JSON.stringify(data.user));
      return data.user;
    } catch {
      this.logout();
      return null;
    }
  }

  logout() {
    localStorage.removeItem("gantt-auth-token");
    localStorage.removeItem("gantt-web-user");
    this.cachedUser = null;
  }

  async startMicrosoftLogin() {
    const msal = getMsal();
    await msal.initialize();
    await msal.loginRedirect({
      scopes: ["openid", "profile", "email"],
    });
  }

  async handleRedirect(): Promise<WebUser | null> {
    const msal = getMsal();
    await msal.initialize();
    const response = await msal.handleRedirectPromise();

    if (response && response.account) {
      const account: AccountInfo = response.account;
      const email = account.username || "";
      const displayName = account.name || email.split("@")[0];
      const microsoftId = (account.idTokenClaims as any)?.oid || account.localAccountId || account.homeAccountId || "";

      return this.microsoftLogin(email, displayName, microsoftId);
    }

    return null;
  }
}

export default WebAuthService;
